import { useLocation } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'src/auth';
import {
  getValidatedFromSessionStorage,
  removeFromSessionStorage,
  setJSONInSessionStorage,
} from 'src/utils/safeStorage';
import { z } from 'zod';

/**
 * Header/palette query text, persisted per TAB: survives a reload, resets on navigation.
 *
 *   type ─▶ setQuery ─▶ sessionStorage (scoped on identity)
 *   reload ──▶ hydrate      navigate ──╳──▶ clear
 *
 * Key is (user, org)-scoped: a raw query can hold a customer name.
 */

const KEY_PREFIX = 'agriful.globalSearch.query';
const LAST_PATH_KEY = 'agriful.globalSearch.lastPath';
const querySchema = z.string().catch('');
const pathnameSchema = z.string();

function storageKeyFor(identity: string | null): string | null {
  return identity ? `${KEY_PREFIX}.${identity}` : null;
}

function loadQuery(storageKey: string | null): string {
  if (typeof window === 'undefined' || !storageKey) return '';
  return getValidatedFromSessionStorage(storageKey, querySchema) ?? '';
}

function loadLastPathname(): string | null {
  if (typeof window === 'undefined') return null;
  return getValidatedFromSessionStorage(LAST_PATH_KEY, pathnameSchema);
}

function storeLastPathname(pathname: string): void {
  if (typeof window === 'undefined') return;
  setJSONInSessionStorage(LAST_PATH_KEY, pathname);
}

// Different pathname than the one last recorded = navigation; same = reload / first load.
function isNavigationTo(pathname: string): boolean {
  const lastPathname = loadLastPathname();
  return lastPathname !== null && lastPathname !== pathname;
}

export interface PersistedSearchQueryState {
  query: string;
  setQuery: (value: string) => void;
}

export function usePersistedSearchQuery(): PersistedSearchQueryState {
  const { currentUser } = useAuth();
  const pathname = useLocation({ select: (location) => location.pathname });

  // Scope on the SAME identity the search key + recents key are minted for, so the
  // query re-keys on login and org-switch and never bleeds across identities.
  const storageKey = useMemo(
    () => storageKeyFor(currentUser ? `${currentUser.id}:${currentUser.organizationId ?? ''}` : null),
    [currentUser]
  );

  const [query, setQueryState] = useState<string>(() => (isNavigationTo(pathname) ? '' : loadQuery(storageKey)));

  const setQuery = useCallback(
    (value: string) => {
      setQueryState(value);
      // No identity ⇒ in-memory only; never persist an unscoped query. An empty
      // query clears the entry rather than storing "" (nothing worth restoring).
      if (!storageKey) return;
      if (value.length === 0) removeFromSessionStorage(storageKey);
      else setJSONInSessionStorage(storageKey, value);
    },
    [storageKey]
  );

  // Clear on navigation, then stamp the current page for the next comparison.
  useEffect(() => {
    if (isNavigationTo(pathname)) setQuery('');
    storeLastPathname(pathname);
  }, [pathname, setQuery]);

  // Re-hydrate on an identity change without a remount (org-switch / re-login).
  const identitySettledRef = useRef(false);
  useEffect(() => {
    if (!identitySettledRef.current) {
      identitySettledRef.current = true;
      return;
    }
    setQueryState(loadQuery(storageKey));
  }, [storageKey]);

  return { query, setQuery };
}
